import React, { useState } from 'react';
import { useWidget } from '../../state/dashboard';
import useFetchMetrics from './useFetchMetrics';
import useDiagDashboard from '../../hooks/useDiagDashboard';
import Drilldown from '../../control/Drilldown';
import RemoveWidget from '../../control/RemoveWidget';
import { Retry } from '@src/components/common';
import { Button, Space, Tooltip } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import Edit from './Edit';
import MetricTrend from './MetricTrend';
import { get } from 'lodash';
import { TrendWrapper } from './styled';
import WidgetCardWrapper from '../../common/WidgetCardWrapper';
import { useAtomValue } from 'jotai';
import { unitAtom } from '@src/components/metrics-panel-hz/atoms';
import { getPercentageMetricByKey, getPercentileMetrics } from '@src/utils/metrics/metricHelpers';
import { isAppModule } from '@src/utils/constants';

function MetricGroup({ wid }) {
  const widgetHandler = useWidget({ wid });
  const { metrics = [], groupName } = widgetHandler.state;
  const unit = useAtomValue(unitAtom);
  const [editMode, setEditMode] = useState(false);
  const showEdit = editMode || metrics.length === 0;

  const { isFetching, isError, data, refetch, queued } = useFetchMetrics({ wid, metrics });

  const { createDashboard } = useDiagDashboard({ metrics, title: groupName });

  const handleEditClick = () => {
    setEditMode((value) => !value);
  };

  const handleCancel = () => {
    setEditMode(false);
  };

  const handleSave = (newState) => {
    widgetHandler.update(newState);
    handleCancel();
  };

  return (
    <WidgetCardWrapper
      wid={wid}
      title={groupName}
      style={{ gridRow: 'span 14' }}
      extra={
        <Space>
          <Tooltip title="Edit">
            <Button icon={<EditOutlined />} onClick={handleEditClick} type="text" />
          </Tooltip>
          <Drilldown createDashboard={createDashboard} disabled={showEdit} />
          <RemoveWidget onClick={widgetHandler.remove} />
        </Space>
      }
    >
      {showEdit ? (
        <Edit
          selectedMetrics={metrics}
          groupName={groupName}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      ) : (
        <Retry
          isLoading={isFetching}
          showError={isError}
          fetchData={refetch}
          progress={true}
          queued={queued}
        >
          <div style={{ position: 'relative', height: '100%' }}>
            <TrendWrapper>
              {metrics.map((metricKey, idx) => {
                let span = 1;
                if (metrics.length < 3) {
                  span = 2;
                } else if (metrics.length === 3) {
                  span = idx === 2 ? 2 : 1;
                }

                const percentageMetric = getPercentageMetricByKey({
                  metricKey,
                  defaultValue: metricKey,
                });
                const chartMetric = unit === '%' ? percentageMetric : metricKey;

                // Check if this specific metric supports percentile
                const isPercentileMetric =
                  isAppModule && getPercentileMetrics().includes(metricKey);
                const percentile = get(data, 'percentile', 'avg');
                const hasPercentileMetric = get(data, 'hasPercentileMetric', false);

                return (
                  <MetricTrend
                    style={{ gridColumn: `span ${span}` }}
                    wid={wid}
                    key={chartMetric}
                    metricKey={chartMetric}
                    data={get(data, ['data', chartMetric], [])}
                    total={get(data, ['totals', chartMetric], null)}
                    percentile={isPercentileMetric && hasPercentileMetric ? percentile : null}
                  />
                );
              })}
            </TrendWrapper>
          </div>
        </Retry>
      )}
    </WidgetCardWrapper>
  );
}

export default MetricGroup;
